import { FunctionComponent, PropsWithChildren, useCallback, useMemo } from "react";
import {
    AuthProvider,
    AwsFilesProvider,
    OidcProvider,
    PermissionsProvider,
    ProfileProvider,
    ToastProvider,
    QueryParamsProvider,
    TerritoryProvider,
} from ".";

type BaseProvider<P = any> = FunctionComponent<PropsWithChildren<P>>;

type ProviderWithProps<P = any> = [BaseProvider<P>, P];

type Provider = BaseProvider | ProviderWithProps;

export const AppProvider = (props: PropsWithChildren) => {
    const combineProviders = useCallback((providers: Provider[]) => {
        return providers.reduce(
            (AccumulatedProviders, CurrentProvider) => {
                if (Array.isArray(CurrentProvider)) {
                    const [Provider, providerProps] = CurrentProvider;
                    return (props: PropsWithChildren) => (
                        <AccumulatedProviders>
                            <Provider {...providerProps}>{props.children}</Provider>
                        </AccumulatedProviders>
                    );
                } else {
                    return (props: PropsWithChildren) => (
                        <AccumulatedProviders>
                            <CurrentProvider>{props.children}</CurrentProvider>
                        </AccumulatedProviders>
                    );
                }
            },
            (props: PropsWithChildren) => <>{props.children}</>
        );
    }, []);

    const providers = useMemo(
        (): Provider[] => [
            ToastProvider,
            AuthProvider,
            OidcProvider,
            ProfileProvider,
            PermissionsProvider,
            AwsFilesProvider,
            TerritoryProvider,
            QueryParamsProvider,
        ],
        []
    );

    return combineProviders(providers)(props);
};
